// @ts-ignore-line
import mapbox, { LngLat, LngLatBounds, Map, MapMouseEvent } from "mapbox-gl";
// import mapbox, { LngLat, LngLatBounds } from "mapbox-gl";
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import posthog from 'posthog-js'
import { MapIds } from "../constants";
import { useSelector } from "./types";
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from './index';
import { MapStyle } from "../types";
import { Layout, PaperSize, BorderStyle } from "../components/mapmaker/types";
import { getPins, onMapClick, PIN_WIDTH_PX } from "../components/mapmaker/pins";
import { BorderInfo } from "../components/mapmaker/constants";
import { createMap } from "./image";
import { clearBorder } from "../components/mapmaker/borders";

export type PinInfo = {
  lat: number;
  lng: number;
};

export enum Step {
  Location = "location",
  Pins = "pins",
  Style = "style",
  Layout = "layout",
  Border = "border",
  Size = "size",
}

export const MIN_ZOOM_LIMIT = 7.5;
export const NUM_PINS_LIMIT = 20;
export const MAP_MAKER_DIV = "mappin-map-div";

export type MapState = {
  map: Map | null;
  geoSearch: HTMLElement | null;
  style: MapStyle;
  layout: Layout;
  border: BorderStyle;
  borderState: Record<string, any> | null;
  size: PaperSize;
  step: Step | null;
  error: string | null;
  pinCount: number; // Track pin count for reactive updates
};

const initialState: MapState = {
  map: null,
  geoSearch: null,
  style: MapStyle.LIGHT,
  layout: Layout.PORTRAIT,
  border: BorderStyle.NONE,
  borderState: null,
  size: PaperSize.A4,
  step: Step.Location,
  error: null,
  pinCount: 0,
};

const getStyleId = (style: MapStyle): string => `mapbox://styles/amansanghvi/${MapIds[style]}`;

// Async thunk for initializing the map
export const initMapAsync = createAsyncThunk(
  'map/initMap',
  async (container: HTMLElement, { getState, dispatch }) => {
    const state = getState() as RootState;
    const mapState = state.map;
    
    if (mapState.map) {
      return mapState.map;
    }

    let bounds: null | LngLatBounds = null;
    const params = new URLSearchParams(window.location.search);
    const maxLat = params.get("maxLat");
    const maxLng = params.get("maxLng");
    const minLat = params.get("minLat");
    const minLng = params.get("minLng");
    if (maxLat && maxLng && minLat && minLng) {
      bounds = new LngLatBounds(
        { lng: parseFloat(minLng), lat: parseFloat(minLat) },
        { lng: parseFloat(maxLng), lat: parseFloat(maxLat) },
      );
    }
    let center: LngLat | null = null;
    const lng = params.get("lng");
    const lat = params.get("lat");
    if (lng && lat) {
      center = new LngLat(parseFloat(lng), parseFloat(lat));
    }

    const newMap = new Map({
      container: container || "",
      style: getStyleId(mapState.style),
      ...(bounds ? { bounds } : { center: center || [2.1686, 41.3874], zoom: 8}),
      attributionControl: false,
      logoPosition: "bottom-right",
      pitchWithRotate: false,
      dragRotate: false,
    });
    
    newMap.touchZoomRotate.disableRotation();
    newMap.on("click", onMapClick(newMap, /* TODO: Clear error */ () => {}));
    newMap.on("zoomend", (event) => {
      dispatch(setError(event.target.getZoom() > MIN_ZOOM_LIMIT ? null : "Zoom level too high - zoom in more"));
    });
    
    return newMap;
  }
);

// Async thunk for submitting the map
export const submitMapAsync = createAsyncThunk(
  'map/submitMap',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { error, border, borderState, layout, size: paperSize, style, map } = state.map;
    
    if (error || !map) {
      throw new Error("Map not ready or has errors");
    }

    if (map?.getZoom() < MIN_ZOOM_LIMIT) {
      throw new Error("Zoom level too low");
    }

    const pins = getPins(map);
    const topLeft = map.unproject([0, 0]);
    const {width: canvasWidth, height: canvasHeight} = map.getCanvas().getBoundingClientRect();
    const bottomRight = map.unproject([
      canvasWidth,
      canvasHeight,
    ]);
    const validPins = pins.filter((pin) =>
      pin.lat > bottomRight.lat
        && pin.lng < bottomRight.lng
        && pin.lat < topLeft.lat
        && pin.lng > topLeft.lng
    );
    
    if (validPins.length > NUM_PINS_LIMIT) {
      throw new Error(`Too many pins (${validPins.length}). Maximum allowed: ${NUM_PINS_LIMIT}`);
    }
    
    const data = {
      pins: validPins,
      pinScale: PIN_WIDTH_PX/canvasWidth,
      minLat: bottomRight.lat,
      minLng: topLeft.lng,
      maxLat: topLeft.lat,
      size: paperSize,
      orientation: layout,
      style,
      borderFilter: BorderInfo[border].id,
      borderState: borderState,
    };
    
    posthog.capture("generate_map_clicked", data);
    dispatch(createMap(data));
    
    return data;
  }
);

// Create the map slice
const mapSlice = createSlice({
  name: 'map',
  initialState,
  reducers: {
    initMap: (state, action: PayloadAction<Map>) => {
      if (!state.map) {
        state.map = action.payload as any;
      }
    },
    closeMap: (state) => {
      state.map = null;
      state.geoSearch = null;
    },
    setStyle: (state, action: PayloadAction<MapStyle>) => {
      state.map?.setStyle(getStyleId(action.payload));
      state.style = action.payload;
    },
    setLayout: (state, action: PayloadAction<Layout>) => {
      // state.map?.resize(); // TODO: Update dimensions
      state.layout = action.payload;
    },
    setSize: (state, action: PayloadAction<PaperSize>) => {
      state.map?.setStyle(action.payload as any); // Note: This looks like a bug in original code
      state.size = action.payload;
    },
    setStep: (state, action: PayloadAction<Step | null>) => {
      state.step = action.payload;
    },
    setBorder: (state, action: PayloadAction<BorderStyle>) => {
      state.border = action.payload;
      if (action.payload !== BorderStyle.NONE) {
        state.borderState = null;
      }
    },
    setBorderState: (state, action: PayloadAction<MapState["borderState"]>) => {
      state.borderState = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    updatePinCount: (state, action: PayloadAction<number>) => {
      state.pinCount = action.payload;
    },
    triggerPriceUpdate: (state) => {
      // This action doesn't change state, it's just used to trigger the middleware
      // The middleware will listen for this action and fetch the price
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle initMapAsync
      .addCase(initMapAsync.fulfilled, (state, action) => {
        if (!state.map) {
          state.map = action.payload as any;
        }
      })
      .addCase(initMapAsync.rejected, (state, action) => {
        state.error = action.error.message || "Failed to initialize map";
      })
      // Handle submitMapAsync
      .addCase(submitMapAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(submitMapAsync.fulfilled, (state) => {
        // Map submission successful - no state changes needed
        state.error = null;
      })
      .addCase(submitMapAsync.rejected, (state, action) => {
        state.error = action.error.message || "Failed to submit map";
      });
  },
});

// Export actions
export const {
  initMap: initMapAction,
  closeMap: closeMapAction,
  setStyle,
  setLayout,
  setSize,
  setStep,
  setBorder: setBorderAction,
  setBorderState: setBorderStateAction,
  setError,
  updatePinCount,
  triggerPriceUpdate,
} = mapSlice.actions;

export const MAPBOX_CONTAINER_ID = "mapbox-gl-js";

mapbox.accessToken = import.meta.env.VITE_MAPBOX_KEY as string;

// Export the reducer
export const mapStore = mapSlice.reducer;

export const closeMap = () => (dispatch: AppDispatch) => {
  dispatch(closeMapAction());
}

export const initMap = (container: HTMLElement) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const mapState = getState().map;
  let bounds: null | LngLatBounds = null;
  const params = new URLSearchParams(window.location.search);
  const maxLat = params.get("maxLat");
  const maxLng = params.get("maxLng");
  const minLat = params.get("minLat");
  const minLng = params.get("minLng");
  if (maxLat && maxLng && minLat && minLng) {
    bounds = new LngLatBounds(
      { lng: parseFloat(minLng), lat: parseFloat(minLat) },
      { lng: parseFloat(maxLng), lat: parseFloat(maxLat) },
    );
  }
  let center: LngLat | null = null;
  const lng = params.get("lng");
  const lat = params.get("lat");
  if (lng && lat) {
    center = new LngLat(parseFloat(lng), parseFloat(lat));
  }

  if (!mapState.map) {
    const newMap = new Map({
      container: container || "",
      style: getStyleId(mapState.style),
      ...(bounds ? { bounds } : { center: center || [2.1686, 41.3874], zoom: 8}),
      attributionControl: false,
      logoPosition: "bottom-right",
      pitchWithRotate: false,
      dragRotate: false,
    });
    newMap.touchZoomRotate.disableRotation();
    newMap.on("click", onMapClick(newMap, /* TODO: Clear error */ () => {}));
    newMap.on("zoomend", (event) => {
      dispatch(setError(event.target.getZoom() > MIN_ZOOM_LIMIT ? null : "Zoom level too high - zoom in more"));
    });
    dispatch(initMapAction(newMap));
    
    // Initialize pin count
    const initialPinCount = getPins(newMap).length;
    dispatch(updatePinCount(initialPinCount));
  }
}

export const attachGeoSearch = (div: HTMLDivElement) => (_: AppDispatch, getState: () => RootState) => {
  const { map } = getState().map;
  if (!map) {
    return;
  }
  const geocoder = new MapboxGeocoder({
    accessToken: import.meta.env.VITE_MAPBOX_KEY as string,
    mapboxgl: mapbox as any,
    placeholder: "Pick your city or area",
    marker: false,
  });
  const el = geocoder.onAdd(map);
  el.style.setProperty("width", "100%");
  el.style.setProperty("max-width", "none");
  for (const child of div.childNodes) {
    div.removeChild(child);
  }
  div.appendChild(el);
  el.focus();
}



export const submitMap = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const { error, border, borderState, layout, size: paperSize, style, map } = getState().map;
  if (error || !map) {
    return;
  }

  if (map?.getZoom() < MIN_ZOOM_LIMIT) {
    return
  }

  const pins = getPins(map);
  const topLeft = map.unproject([0, 0]);
  const {width: canvasWidth, height: canvasHeight} = map.getCanvas().getBoundingClientRect();
  const bottomRight = map.unproject([
    canvasWidth,
    canvasHeight,
  ]);
  const validPins = pins.filter((pin) =>
    pin.lat > bottomRight.lat
      && pin.lng < bottomRight.lng
      && pin.lat < topLeft.lat
      && pin.lng > topLeft.lng
  );
  if (validPins.length > NUM_PINS_LIMIT) {
    return;
  }
  const data = {
    pins: validPins,
    pinScale: PIN_WIDTH_PX/canvasWidth,
    minLat: bottomRight.lat,
    minLng: topLeft.lng,
    maxLat: topLeft.lat,
    size: paperSize,
    orientation: layout,
    style,
    borderFilter: BorderInfo[border].id,
    borderState: borderState,
  };
  posthog.capture("generate_map_clicked", data);
  dispatch(createMap(data));
}

// Update setBorder to use the new action creator
export const setBorder = (border: BorderStyle) => (dispatch: AppDispatch) => {
  if (border === BorderStyle.NONE) clearBorder();
  dispatch(setBorderAction(border));
};

// Update setBorderState to use the new action creator
export const setBorderState =
  (borderStateValue: MapState["borderState"], drawFn: (map: Map, state: MapState["borderState"]) => void) => (dispatch: AppDispatch, getState: () => RootState) => {
    const { map } = getState().map;
    clearBorder();
    if (!map) return;
    drawFn(map, borderStateValue);
    dispatch(setBorderStateAction(borderStateValue));
  };

// Export setMapError as alias for setError
export const setMapError = setError;

export const useMap = (): Map | null => useSelector((state) => state.map.map);

export const useStyle = (): MapStyle => useSelector((state) => state.map.style);
export const useLayout = (): Layout => useSelector((state) => state.map.layout);
export const useSize = (): PaperSize => useSelector((state) => state.map.size);
export const useBorder = (): BorderStyle => useSelector((state) => state.map.border);
export const useBorderState = () => useSelector((state) => state.map.borderState);
export const useStep = (): Step => useSelector((state) => state.map.step);
export const useMapError = (): string | null => useSelector((state) => state.map.error);
export const usePinCount = (): number => useSelector((state) => state.map.pinCount);
